import { useEffect, useRef, useState } from "react";
import { CheckCircle2, FileUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

// Build 3 · Opción terminal del arrendamiento: piezas compartidas por las ventanas.
export const MEDIOS_OT = ["Transferencia SPEI", "Efectivo", "Domiciliación", "Cheque", "Tarjeta"];
export const MEDIO_INICIAL = { medio_pago: "Transferencia SPEI", banco: "", referencia: "" };
export const rutaOt = (cid, accion = "") => `/arrendamiento/creditos/${cid}/opcion-terminal${accion ? `/${accion}` : ""}`;

export function FilasResumen({ filas, testid }) {
  return (
    <dl className="divide-y divide-slate-100 overflow-hidden rounded-md border border-slate-200 bg-white text-sm" data-testid={testid}>
      {filas.filter(Boolean).map(([l, v, fuerte]) => (
        <div key={l} className="flex items-start justify-between gap-4 px-3 py-1.5">
          <dt className="text-slate-500">{l}</dt>
          <dd className={`text-right tabular-nums ${fuerte ? "font-semibold text-slate-900" : "text-slate-700"}`}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function MedioPagoCampos({ valor, onChange, prefijo }) {
  const set = (k, v) => onChange({ ...valor, [k]: v });
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div>
        <Label className="mb-1 block text-xs">Medio de pago</Label>
        <Select value={valor.medio_pago} onValueChange={(v) => set("medio_pago", v)}>
          <SelectTrigger data-testid={`${prefijo}-medio`}><SelectValue /></SelectTrigger>
          <SelectContent>{MEDIOS_OT.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label className="mb-1 block text-xs">Banco</Label>
        <Input data-testid={`${prefijo}-banco`} value={valor.banco} onChange={(e) => set("banco", e.target.value)} /></div>
      <div><Label className="mb-1 block text-xs">Referencia</Label>
        <Input data-testid={`${prefijo}-referencia`} value={valor.referencia} onChange={(e) => set("referencia", e.target.value)} /></div>
    </div>
  );
}

export function SubirPdfOt({ creditoId, tipo, etiqueta, doc, onDoc, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const subir = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!(f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"))) { toast.error("Sube un archivo PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("tipo", tipo);
      const { data } = await api.post(rutaOt(creditoId, "documento"), fd);
      onDoc(data);
      toast.success(`${etiqueta}: cargada`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2" data-testid={testid}>
      <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" onChange={subir} data-testid={`${testid}-input`} />
      <div className="min-w-0 text-xs">
        {doc
          ? <span className="flex items-center gap-1.5 truncate text-emerald-700" data-testid={`${testid}-cargado`}><CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> {doc.nombre_archivo}</span>
          : <span className="text-slate-500">{etiqueta} <span className="text-red-600">*</span> (PDF)</span>}
      </div>
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testid}-btn`}>
        {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <FileUp className="mr-1.5 h-3.5 w-3.5" />}
        {doc ? "Reemplazar" : "Subir PDF"}
      </Button>
    </div>
  );
}

export function useAccionOt(creditoId, onDone) {
  const [busy, setBusy] = useState(false);
  const ejecutar = async (accion, body, ok) => {
    setBusy(true);
    try {
      const { data } = await api.post(rutaOt(creditoId, accion), body);
      toast.success(ok);
      onDone?.(data); // sin esperar: la ventana se cierra y el bloque se refresca detrás
      return true;
    } catch (e) { toast.error(apiError(e)); return false; } finally { setBusy(false); }
  };
  return { busy, ejecutar };
}

// Ventana en dos pasos: captura → confirmación con resumen antes de ejecutar.
export function OtDialog({ open, onOpenChange, titulo, descripcion, form, resumen, puedeRevisar, onConfirmar, busy,
  textoConfirmar, testid, destructivo = false }) {
  const [paso, setPaso] = useState("form");
  useEffect(() => { if (open) setPaso("form"); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-heading">{titulo}{paso === "confirm" ? " · confirma" : ""}</DialogTitle>
          <DialogDescription>{paso === "confirm" ? "Revisa el resumen: al confirmar se ejecuta y queda en la bitácora." : descripcion}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3" data-testid={`${testid}-${paso}`}>{paso === "form" ? form : resumen}</div>
        <DialogFooter className="gap-2">
          {paso === "form" ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button data-testid={`${testid}-revisar`} disabled={!puedeRevisar} onClick={() => setPaso("confirm")}>Revisar</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPaso("form")} disabled={busy}>Volver</Button>
              <Button data-testid={`${testid}-confirmar`} variant={destructivo ? "destructive" : "default"} disabled={busy} onClick={onConfirmar}>
                {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} {textoConfirmar}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
